/**
 * Date and Time Formatter Utility
 *
 * Provides centralized date/time formatting that respects user settings:
 * - Timezone preference
 * - Date format preference (MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD)
 * - Time format preference (12h vs 24h)
 * - Language-specific localization
 *
 * This ensures consistent date/time display throughout the application.
 *
 * @example
 * ```tsx
 * import { useDateTimeFormatter } from '@shared/utils/dateTimeFormatter';
 *
 * function MyComponent() {
 *   const { formatDate, formatTime, formatDateTime } = useDateTimeFormatter();
 *
 *   return (
 *     <div>
 *       <p>Date: {formatDate(new Date())}</p>
 *       <p>Time: {formatTime(new Date())}</p>
 *       <p>Full: {formatDateTime(new Date())}</p>
 *     </div>
 *   );
 * }
 * ```
 */

import { useSettings } from "@shared/contexts/SettingsContext";
import logger from "@shared/utils/logger";

/**
 * Hook that provides date/time formatting functions respecting user settings
 */
export const useDateTimeFormatter = () => {
  const { settings } = useSettings();

  /**
   * Format a date according to user's date format and timezone preferences
   * @param date - Date to format (Date object or ISO string)
   * @returns Formatted date string
   */
  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return "—";

    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return "Invalid Date";

      const locale = getLocaleFromLanguage(settings.language || "en");

      return new Intl.DateTimeFormat(locale, {
        timeZone: settings.timezone || "UTC",
        ...getDateStyle(settings.date_format || "MM/DD/YYYY"),
      }).format(d);
    } catch (error) {
      logger.error("[dateTimeFormatter] Error formatting date:", error);
      return "Error";
    }
  };

  /**
   * Format a time according to user's time format and timezone preferences
   * @param date - Date to extract time from (Date object or ISO string)
   * @returns Formatted time string
   */
  const formatTime = (date: Date | string | null | undefined): string => {
    if (!date) return "—";

    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return "Invalid Time";

      const locale = getLocaleFromLanguage(settings.language || "en");

      return new Intl.DateTimeFormat(locale, {
        timeZone: settings.timezone || "UTC",
        hour: "2-digit",
        minute: "2-digit",
        hour12: settings.time_format === "12h",
      }).format(d);
    } catch (error) {
      logger.error("[dateTimeFormatter] Error formatting time:", error);
      return "Error";
    }
  };

  /**
   * Format both date and time
   * @param date - Date to format (Date object or ISO string)
   * @returns Formatted date and time string
   */
  const formatDateTime = (date: Date | string | null | undefined): string => {
    if (!date) return "—";

    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return "Invalid DateTime";

      const locale = getLocaleFromLanguage(settings.language || "en");

      return new Intl.DateTimeFormat(locale, {
        timeZone: settings.timezone || "UTC",
        ...getDateStyle(settings.date_format || "MM/DD/YYYY"),
        hour: "2-digit",
        minute: "2-digit",
        hour12: settings.time_format === "12h",
      }).format(d);
    } catch (error) {
      logger.error("[dateTimeFormatter] Error formatting datetime:", error);
      return "Error";
    }
  };

  /**
   * Format a date as a relative time string (e.g., "2 hours ago", "yesterday")
   * @param date - Date to format
   * @returns Relative time string
   */
  const formatRelativeTime = (
    date: Date | string | null | undefined,
  ): string => {
    if (!date) return "—";

    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return "Invalid Date";

      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffSecs < 60) return "just now";
      if (diffMins < 60)
        return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
      if (diffDays < 7)
        return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;

      // For older dates, fall back to formatted date
      return formatDate(d);
    } catch (error) {
      logger.error(
        "[dateTimeFormatter] Error formatting relative time:",
        error,
      );
      return "Error";
    }
  };

  return { formatDate, formatTime, formatDateTime, formatRelativeTime };
};

/**
 * Map language code to Intl.DateTimeFormat locale
 */
const getLocaleFromLanguage = (lang: string): string => {
  const map: Record<string, string> = {
    en: "en-US",
    es: "es-ES",
    de: "de-DE",
    fr: "fr-FR",
  };
  return map[lang] || "en-US";
};

/**
 * Map date format preference to Intl.DateTimeFormat options
 */
const getDateStyle = (format: string) => {
  switch (format) {
    case "MM/DD/YYYY":
      return { month: "2-digit", day: "2-digit", year: "numeric" } as const;
    case "DD/MM/YYYY":
      return { day: "2-digit", month: "2-digit", year: "numeric" } as const;
    case "YYYY-MM-DD":
      return { year: "numeric", month: "2-digit", day: "2-digit" } as const;
    default:
      return { dateStyle: "medium" as const };
  }
};

/**
 * Standalone function for formatting dates without hook (for use outside React components)
 */
export const formatDateStandalone = (
  date: Date | string | null | undefined,
  options?: {
    timezone?: string;
    dateFormat?: string;
    language?: string;
  },
): string => {
  if (!date) return "—";

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "Invalid Date";

    const locale = getLocaleFromLanguage(options?.language || "en");

    return new Intl.DateTimeFormat(locale, {
      timeZone: options?.timezone || "UTC",
      ...getDateStyle(options?.dateFormat || "MM/DD/YYYY"),
    }).format(d);
  } catch (error) {
    logger.error(
      "[dateTimeFormatter] Error formatting date (standalone):",
      error,
    );
    return "Error";
  }
};

export default useDateTimeFormatter;
